import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Plus, Edit, Trash2, Save, X, Search } from "lucide-react";
import { useAuth } from "../../utils/auth";
import { isAdmin } from "../../utils/roles";
import {
  fetchColors,
  createColor,
  updateColor,
  deleteColor,
} from "../../api/CatalogAPIs";
import {
  CatalogShell,
  ConfirmDialog,
  TableSkeleton,
  EmptyState,
  Switch,
  parseBilingual,
  joinBilingual,
  displayAr,
  displayEn,
  normalizeHex,
} from "./catalogUi";

const emptyForm = { nameAr: "", nameEn: "", hexCode: "#000000", isActive: true };

const ColorsPage = () => {
  const navigate = useNavigate();
  const { authState } = useAuth();
  const canManage = isAdmin(authState.role);

  const [search, setSearch] = useState("");
  const [formMode, setFormMode] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const { data = [], isLoading, error, refetch } = useQuery({
    queryKey: ["catalogColors"],
    queryFn: fetchColors,
    enabled: canManage,
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!authState.isValidating && !canManage) navigate("/login");
  }, [authState, canManage, navigate]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return data;
    return data.filter((item) => {
      const blob = `${item.name || ""} ${item.hexCode || ""}`.toLowerCase();
      return blob.includes(term);
    });
  }, [data, search]);

  const openCreate = () => {
    setFormMode("create");
    setEditing(null);
    setForm(emptyForm);
  };

  const openEdit = (item) => {
    const names = parseBilingual(item.name);
    setFormMode("edit");
    setEditing(item);
    setForm({
      nameAr: names.ar,
      nameEn: names.en,
      hexCode: normalizeHex(item.hexCode) || "#000000",
      isActive: item.isActive !== false,
    });
  };

  const closeForm = () => {
    setFormMode(null);
    setEditing(null);
    setForm(emptyForm);
  };

  const setHex = (value) => {
    const next = normalizeHex(value);
    setForm((prev) => ({ ...prev, hexCode: next || value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const name = joinBilingual(form.nameAr, form.nameEn);
    if (!name) {
      toast.error("اسم اللون مطلوب");
      return;
    }
    const hex = normalizeHex(form.hexCode);
    if (hex && !/^#[0-9A-F]{6}$/.test(hex)) {
      toast.error("كود اللون غير صالح");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name,
        hexCode: hex || null,
        isActive: form.isActive,
      };
      if (formMode === "create") {
        await createColor(payload);
        toast.success("تم إضافة اللون");
      } else {
        await updateColor(editing.colorId, payload);
        toast.success("تم تحديث اللون");
      }
      closeForm();
      refetch();
    } catch (err) {
      toast.error(err.message || "فشل الحفظ");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteColor(deleteTarget.colorId);
      toast.success("تم حذف اللون");
      setDeleteTarget(null);
      refetch();
    } catch (err) {
      toast.error(err.message || "تعذر الحذف — قد يكون اللون مستخدماً في منتج");
    } finally {
      setDeleting(false);
    }
  };

  if (!canManage) return null;

  const pickerValue = /^#[0-9A-Fa-f]{6}$/.test(form.hexCode) ? form.hexCode : "#000000";

  return (
    <CatalogShell title="الألوان" subtitle="كتالوج الألوان المشترك لمتغيرات المنتجات الجاهزة">
      <ToastContainer position="top-center" />
      <div className="flex flex-col md:flex-row gap-3 justify-between mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute right-3 top-3.5 text-gray-400" size={18} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="بحث عن لون..."
            className="w-full pr-10 pl-4 py-3 rounded-xl border border-gray-200 bg-white"
          />
        </div>
        <button
          onClick={openCreate}
          className="px-5 py-3 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-bold flex items-center gap-2 shadow"
        >
          <Plus size={18} /> لون جديد
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-2xl mb-6">
          فشل تحميل الألوان.
        </div>
      )}

      <div className="bg-white/90 rounded-2xl shadow border border-white/30 overflow-hidden">
        {isLoading ? (
          <TableSkeleton />
        ) : filtered.length === 0 ? (
          <EmptyState text="لا توجد ألوان" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-right min-w-[640px]">
              <thead className="bg-gray-50 text-sm text-gray-600">
                <tr>
                  <th className="p-3">معاينة</th>
                  <th className="p-3">الاسم عربي</th>
                  <th className="p-3">الاسم إنجليزي</th>
                  <th className="p-3">Hex</th>
                  <th className="p-3">الحالة</th>
                  <th className="p-3">إجراءات</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((item) => (
                  <tr key={item.colorId} className="border-t border-gray-100">
                    <td className="p-3">
                      <span
                        className="inline-block w-8 h-8 rounded-full border border-gray-200 shadow-inner"
                        style={{ backgroundColor: item.hexCode || "#ddd" }}
                        title={item.hexCode}
                      />
                    </td>
                    <td className="p-3 font-medium">{displayAr(item.name)}</td>
                    <td className="p-3 text-gray-600">{displayEn(item.name)}</td>
                    <td className="p-3 font-mono text-sm" dir="ltr">
                      {item.hexCode || "—"}
                    </td>
                    <td className="p-3">
                      <span
                        className={`px-2 py-1 rounded-full text-sm ${
                          item.isActive ? "bg-green-100 text-green-700" : "bg-gray-200 text-gray-600"
                        }`}
                      >
                        {item.isActive ? "نشط" : "معطّل"}
                      </span>
                    </td>
                    <td className="p-3">
                      <div className="flex gap-2">
                        <button
                          onClick={() => openEdit(item)}
                          className="p-2 rounded-lg bg-indigo-50 text-indigo-700"
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          onClick={() => setDeleteTarget(item)}
                          className="p-2 rounded-lg bg-red-50 text-red-700"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {formMode && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold">{formMode === "create" ? "لون جديد" : "تعديل اللون"}</h3>
              <button onClick={closeForm}>
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="text-sm text-gray-600">الاسم عربي</label>
                <input
                  value={form.nameAr}
                  onChange={(e) => setForm({ ...form, nameAr: e.target.value })}
                  className="w-full mt-1 px-3 py-2 border-2 border-gray-200 rounded-xl"
                />
              </div>
              <div>
                <label className="text-sm text-gray-600">الاسم إنجليزي</label>
                <input
                  value={form.nameEn}
                  onChange={(e) => setForm({ ...form, nameEn: e.target.value })}
                  className="w-full mt-1 px-3 py-2 border-2 border-gray-200 rounded-xl"
                  dir="ltr"
                />
              </div>
              <div>
                <label className="text-sm text-gray-600">اللون</label>
                <div className="mt-1 flex items-center gap-3">
                  <input
                    type="color"
                    value={pickerValue}
                    onChange={(e) => setHex(e.target.value)}
                    className="w-14 h-12 rounded-lg border cursor-pointer"
                  />
                  <input
                    value={form.hexCode}
                    onChange={(e) => setHex(e.target.value)}
                    className="flex-1 px-3 py-2 border-2 border-gray-200 rounded-xl font-mono"
                    dir="ltr"
                    placeholder="#000000"
                  />
                  <span
                    className="w-10 h-10 rounded-full border"
                    style={{ backgroundColor: pickerValue }}
                  />
                </div>
              </div>
              <Switch
                checked={form.isActive}
                onChange={(checked) => setForm({ ...form, isActive: checked })}
                label="نشط"
              />
              <button
                type="submit"
                disabled={saving}
                className="w-full py-3 rounded-xl bg-indigo-600 text-white font-semibold flex items-center justify-center gap-2"
              >
                <Save size={16} /> {saving ? "جاري الحفظ..." : "حفظ"}
              </button>
            </form>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        title="حذف اللون"
        message={`هل تريد حذف "${displayAr(deleteTarget?.name)}"؟`}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        loading={deleting}
      />
    </CatalogShell>
  );
};

export default ColorsPage;
